import { captureEvent } from "@sentry/react";
import {
  CreateGlobalCreditAccountDocument,
  type CreateGlobalCreditAccountInput,
  type CreateGlobalCreditAccountMutation,
  type CreateGlobalCreditAccountMutationVariables,
  DeleteGlobalCreditAccountDocument,
  type DeleteGlobalCreditAccountMutation,
  type DeleteGlobalCreditAccountMutationVariables,
  type GlobalCreditAccountFragment,
  GlobalCreditAccountOrderByColumnEnum,
  GlobalCreditAccountsDocument,
  type GlobalCreditAccountsQuery,
  type GlobalCreditAccountsQueryVariables,
  type GlobalCreditAccountsWhereColumn,
  SortOrder,
  UpdateGlobalCreditAccountDocument,
  type UpdateGlobalCreditAccountInput,
  type UpdateGlobalCreditAccountMutation,
  type UpdateGlobalCreditAccountMutationVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { commonSearchParams } from "@src/utils/apolloHelpers";
import { Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { getParamFromQuery } from "@src/utils/url";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import { GlobalCreditAccountModalStore } from "../components/modal/store";

type DeleteModalData = {
  id: string;
};

export class GlobalAccountsListingStore {
  readonly moduleKey = "global-credit-accounts";

  @observable
  data: GlobalCreditAccountFragment[] = [];

  @observable.ref
  searchTerm: string;

  modalStore: GlobalCreditAccountModalStore;
  deleteModalDisclosure = new DisclosureState<DeleteModalData>();

  pagination = new PaginationState(this.moduleKey, {
    onChangePagination: ({ asParams }) => {
      this.fetchData(asParams);
    },
  });
  orderBy = new OrderBy([
    { column: GlobalCreditAccountOrderByColumnEnum.Id, order: SortOrder.Desc },
  ]);
  where = new Filters<GlobalCreditAccountsWhereColumn>([]);

  private fetcher = new FetchHelper<
    GlobalCreditAccountsQuery,
    GlobalCreditAccountsQueryVariables
  >(GlobalCreditAccountsDocument);
  private createMutator = new MutationHelper<
    CreateGlobalCreditAccountMutation,
    CreateGlobalCreditAccountMutationVariables
  >(CreateGlobalCreditAccountDocument);
  private updateMutator = new MutationHelper<
    UpdateGlobalCreditAccountMutation,
    UpdateGlobalCreditAccountMutationVariables
  >(UpdateGlobalCreditAccountDocument);
  private deleteMutator = new MutationHelper<
    DeleteGlobalCreditAccountMutation,
    DeleteGlobalCreditAccountMutationVariables
  >(DeleteGlobalCreditAccountDocument);

  constructor(private appStore: AppStore) {
    makeObservable(this);
    this.modalStore = new GlobalCreditAccountModalStore(appStore);

    this.where.fromURLSearchParam(Router.query);
    this.orderBy.fromURLSearchParam(Router.query);
    this.pagination.fromURLSearchParam(Router.query);
    this.searchTerm = getParamFromQuery("search", Router.query) ?? "";
  }

  @computed
  get searchParams() {
    return commonSearchParams(this);
  }

  @computed
  get isFetching() {
    return this.fetcher.isLoading.value;
  }

  @computed
  get isModalLoading() {
    return (
      this.createMutator.isLoading.value ||
      this.updateMutator.isLoading.value ||
      this.deleteMutator.isLoading.value
    );
  }

  @action.bound
  async fetchData(pagination?: PaginationState["asParams"]) {
    const [data, error] = await this.fetcher.fetch({
      page: pagination?.page ?? this.pagination.asParams.page,
      first: pagination?.first ?? this.pagination.asParams.first,
      filters: {
        orderBy: this.orderBy.asWhereParam,
        search: this.searchTerm,
      },
    });

    if (error) return;

    this.data = data?.globalCreditAccounts?.data ?? [];
  }

  @action.bound
  handleChangeSearch(search: string) {
    this.searchTerm = search;
    this.pagination.resetPage();
  }

  @computed
  get activeOrderBy() {
    if (!this.orderBy.clauses.length) return undefined;
    return this.orderBy.clauses[0];
  }

  @action.bound
  handleChangeOrderBy(orderBy: {
    column: GlobalCreditAccountOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!orderBy.order) {
      this.orderBy.setClauses([]);
      this.fetchData();
      return;
    }
    this.orderBy.setClauses([{ column: orderBy.column, order: orderBy.order }]);
    this.fetchData();
  }

  @action.bound
  handleCreateCreditAccount() {
    this.modalStore.modalState.onOpen({
      onSubmit: this.createCreditAccount,
    });
  }

  @action.bound
  private async createCreditAccount(input: CreateGlobalCreditAccountInput) {
    const [, error] = await this.createMutator.mutate({ input });

    if (error) return;

    this.modalStore.modalState.onClose();
    this.fetchData();
  }

  @action.bound
  handleEditCreditAccount(creditAccount: GlobalCreditAccountFragment) {
    this.modalStore.modalState.onOpen({
      globalCreditAccount: creditAccount,
      onSubmit: (input) => {
        if (!("id" in input)) return;

        this.updateCreditAccount(input);
      },
    });
  }

  @action.bound
  private async updateCreditAccount(input: UpdateGlobalCreditAccountInput) {
    const [, error] = await this.updateMutator.mutate({ input });

    if (error) return;

    this.modalStore.modalState.onClose();
    this.fetchData();
  }

  @action.bound
  handleDeleteCreditAccount(id: string) {
    this.deleteModalDisclosure.onOpen({
      id,
    });
  }

  @action.bound
  handleDeleteModalConfirm() {
    if (!this.deleteModalDisclosure.additionalData?.id) {
      captureEvent({
        message: "FE: Missing ID to delete global credit account.",
      });
      this.appStore.UIStore.toast({
        status: "error",
      });
      return;
    }

    this.deleteCreditAccount(this.deleteModalDisclosure.additionalData.id);
  }

  @action.bound
  private async deleteCreditAccount(id: string) {
    const [, error] = await this.deleteMutator.mutate({ id });

    if (error) return;

    this.deleteModalDisclosure.onClose();
    this.pagination.resetPage();
  }
}
